import { createSlice } from '@reduxjs/toolkit';
import { ActionType } from '../models/action-type';
import { AlbumPage } from '../layout-editor/albam/albam';
import { AddAlbumParam } from '../components/pages/layout/layout';
import { OrderPageDataXml } from '../xml/class/order/order-page-data-xml';
import { PageDataTypesResponse } from '../models/api/back/create-order/page-data-types';
import { RestorePicData } from '../layout-editor/model/layout.xml.utile';
import { MetaOrderGetResponse } from '../api/front/meta/api-meta-order';
import { GuideType } from '../components/pages/layout/toolbar/layout.trimming-controller';
import { TrimPrint } from '../trim-editor/print/trim.print';

export type TrimItemData = {
  // templateGroup: string,
  // print: string,
  id: string,
  templateName: string,
  thumbnail: string,
  templateXmlUrl: string,
  targetPath: string,
  favTempId?: string,
};

export type TrimTemplateFolderData = {
  id: string,
  label: string,
  type: 'file' | 'directory' | 'template',
  children: TrimTemplateFolderData[],
  pageType?: string,
};

export type OpenTemplateType = 'all' | 'favorite' | '';
export type OpenTemplateInfo = {
  type: 'all' | 'favorite' | ''
  favoriteFolderID: string;
}

export type TrimPageType = 'select' | 'list' | 'edit';

// type PageType = 'cover' | 'top' | 'print' | 'end-cover';
type PageType = string;

export type AlertPathData = {
  pageType: string,
  pathData: string[]
}

export type TrimPageData = {
  page: number,
  albums: TrimPrint[],
  pageCount: number,
  pageTypeId: string,
  displayPageNo?: string,
  displayPageType?: string,
};

type DeleteTrimPageData = {
  thumbnailID: string,
  templatePath: string,
}

export type PrintSettingData = {
  productName: string,
  name: string,
  date: string,
  formatType: string,
  initialType: string,
  location: string,
  pageTypes: string[],
  pageCount: string,
  stepPageCount: string,
  pageCountInfo: {
    step: string,
    max: string,
    min: string,
  },
};

export type PageRequestMemo = {
  page: string,
  memo: string,
};

export type TemplatesInfo = {
  name: string,
  vertical: string,
  horizon: string,
  sq: string,
  pageType: string,
};

export type AcquiredTempInfo =
  {
    // id: string,
    // tempName: string,
    itemData: TrimItemData,
    templates: { xml: any, pageType: string },
    cover?: { min: string, max: string },
    template: any,
    thumbnail: string,
    zipLevel: {
      isZip: boolean,
      level: number,
    },
  };

export type TemplateSearchRequest = {
  sum: string,
  vertical: string,
  horizon: string,
  sq: string,
  searchTypeNum: string,
};

export type TrimQueryParams = {
  productNameId: string,
};

export type PreviewSelectItem = {
  pageNum: string,
  album: AlbumPage,
};


export type PresetData = {
  name: string,
  windowSize: string,
  humanScale: string,
  topSpace: string,
  bottomSpace: string,
}

export type TrimPageDataList = {
  cover: TrimPageData,
  top: TrimPageData,
  page: TrimPageData,
  endCover: TrimPageData,
  option: {
    name: string,
    xml: OrderPageDataXml,
    album: TrimPrint | null,
  }[],
  opPrint: {
    name: string,
    xml: OrderPageDataXml,
    album: TrimPrint | null,
  }[],
}

export type SaveSelectData = {
  selectId: string,
  editableImageId: string,
}

export type PageTypeData = {
  pageTypeID: string,
  type: string,
}

export type PageTypeListData = {
  cover: PageTypeData[],
  top: PageTypeData[],
  page: PageTypeData[],
  endCover: PageTypeData[],
  option: PageTypeData[],
  opPrint: PageTypeData[],
}

export type SortPageTypeDataEntity = {
  $: Partial<SortPageType>;
}
export type SortPageType = {
  pageTypeID: string;
  name: string;
  displayName: string;
}

type TrimState = {
  layoutList: TrimItemData[],
  folderList: TrimTemplateFolderData[],
  selectFolder: TrimTemplateFolderData,
  page: TrimPageType,
  thumbnailPath: string,
  albumPages: TrimPageDataList,
  currentAlbumPage: TrimPrint | null,
  albumSettingData: PrintSettingData,
  pageRequestMemo: PageRequestMemo[],
  templatesInfoList: TemplatesInfo[],
  isDropTemplate: boolean,
  isDraggingTemplate: boolean,
  acquiredTemplateList: AcquiredTempInfo[],
  queryParams: TrimQueryParams | null,
  previewSelectItem: PreviewSelectItem | null,
  presetList: PresetData[],
  preset: PresetData | null,
  templateGroupName: string,
  templatesXmlList: any[],
  deleteAlbumInfos: DeleteTrimPageData[],
  openTemplateType: OpenTemplateInfo,
  isStopExifWarning: boolean,
  isStopTextPosWarning: boolean,
  isSaving: boolean,
  restoreTemplates: AddAlbumParam[],
  /* 商品記載情報記載箇所チェックボックス一覧 */
  pageDataTypesRes: PageDataTypesResponse,
  unfindFontList: string[],
  initFontList: { value: string, label: string }[],
  trimmingGuideList: GuideType[],
  metaData: MetaOrderGetResponse | null,
  restorePicData: RestorePicData[],
  alertPathList: AlertPathData[],
  saveSelect: SaveSelectData[],
  pageTypeList: PageTypeListData,
  isSelectIDError: Boolean,
  warningList: string[],
  fitErrorList: string[],
  fontErrorList: string[],
};

const initialState: TrimState = {
  layoutList: [],
  folderList: [],
  unfindFontList: [],
  selectFolder: {
    id: '',
    label: '',
    type: 'template',
    children: [],
  },
  thumbnailPath: '',
  page: 'select',
  albumPages: {
    cover: {
      page: 0,
      albums: [],
      pageCount: 0,
      pageTypeId: '',
    },
    top: {
      page: 0,
      albums: [],
      pageCount: 0,
      pageTypeId: '',
    },
    page: {
      page: 0,
      albums: [],
      pageCount: 0,
      pageTypeId: '',
    },
    endCover: {
      page: 0,
      albums: [],
      pageCount: 0,
      pageTypeId: '',
    },
    option: [],
    opPrint: [],
  },
  currentAlbumPage: null,
  albumSettingData: {
    productName: '写真アルバムプロ A3SQ HC',
    name: '',
    date: '',
    formatType: '10',
    initialType: '0',
    location: '',
    pageTypes: [],
    pageCount: '6',
    stepPageCount: '2',
    pageCountInfo: {
      step: '2',
      max: '60',
      min: '6',
    },
  },
  pageRequestMemo: [],
  templatesInfoList: [],
  isDropTemplate: false,
  isDraggingTemplate: false,
  acquiredTemplateList: [],
  queryParams: null,
  previewSelectItem: null,
  presetList: [],
  preset: null,
  templateGroupName: '',
  templatesXmlList: [],
  deleteAlbumInfos: [],
  openTemplateType: {
    type: '',
    favoriteFolderID: '',
  },
  isStopExifWarning: false,
  isStopTextPosWarning: false,
  isSaving: false,
  restoreTemplates: [],
  pageDataTypesRes: { pageDataTypes: [] },
  initFontList: [],
  trimmingGuideList: [],
  metaData: null,
  restorePicData: [],
  alertPathList: [],
  saveSelect: [],
  pageTypeList: {
    cover: [],
    top: [],
    page: [],
    endCover: [],
    option: [],
    opPrint: [],
  },
  isSelectIDError: false,
  warningList: [],
  fitErrorList: [],
  fontErrorList: [],
};

const setTemplateList: ActionType<TrimState, TrimItemData[]> = (state, action) => {
  state.layoutList = action.payload;
};

const addTemplateList: ActionType<TrimState, TrimItemData[]> = (state, action) => {
  state.layoutList = [...state.layoutList, ...action.payload];
};

const setFolderList: ActionType<TrimState, TrimTemplateFolderData[]> = (state, action) => {
  state.folderList = action.payload;
};

const addFolderList: ActionType<TrimState, TrimTemplateFolderData[]> = (state, action) => {
  state.folderList = [...state.folderList, ...action.payload];
};

const setSelectFolder: ActionType<TrimState, TrimTemplateFolderData> = (state, action) => {
  state.selectFolder = action.payload;
};
const setTrimmingGuideList: ActionType<TrimState, GuideType[]> = (state, action) => {
  state.trimmingGuideList = action.payload;
};
const pushUnFindFontList: ActionType<TrimState, string | null> = (state, action) => {
  if (action.payload) {
    state.unfindFontList = [...state.unfindFontList, action.payload];
  } else {
    state.unfindFontList = [];
  }
};

const setPage: ActionType<TrimState, TrimPageType> = (state, action) => {
  if (state.page !== action.payload) {
    state.page = action.payload;
  }
};
const setInitFontList: ActionType<TrimState, { value: string, label: string }[]> = (state, action) => {
  state.initFontList = action.payload;
};

const setAlbumData: ActionType<TrimState, {
  cover: TrimPageData,
  top: TrimPageData,
  page: TrimPageData,
  endCover: TrimPageData,
  option: {
    name: string,
    xml: OrderPageDataXml,
    album: TrimPrint | null,
  }[],
  opPrint: {
    name: string,
    xml: OrderPageDataXml,
    album: TrimPrint | null,
  }[],
}> = (state, action) => {
  state.albumPages = action.payload;
  // {
  // cover: {
  //   print: 1,
  //   albums: [],
  // },
  // top: {
  //   print: 1,
  //   albums: [],
  // },
  // print: {
  //   print: 2,
  //   albums: [],
  // },
  // endCover: {
  //   print: 1,
  //   albums: [],
  // },
  // };
};

const setOptionName: ActionType<TrimState, {
  optionType: 'option' | 'opPrint',
  name: string,
  pageType: string
}> = (state, action) => {
  if (action.payload.optionType === 'option') {
    state.albumPages.option.forEach(v => {
      if (v.xml.viewModel.pageType === action.payload.pageType) {
        v.name = action.payload.name;
      }
    });
  } else {
    state.albumPages.opPrint.forEach(v => {
      if (v.xml.viewModel.pageType === action.payload.pageType) {
        v.name = action.payload.name;
      }
    });
  }
};

const setMetaData: ActionType<TrimState, MetaOrderGetResponse> = (state, action) => {
  state.metaData = action.payload;
};

const sortAlbumPage: ActionType<TrimState, { dragItem: TrimPrint, dropPosItem: TrimPrint }> = (state, action) => {
  // const moveItem = action.payload.dragItem;
  // const targetPosItem = action.payload.dropPosItem;
  // const page = state.albumPages.page.albums.map((v) => ({ pageNo: v.pageNo, indexes: v.indexes }));
  // if (moveItem.type.indexOf('page') !== -1 && targetPosItem.type.indexOf('page') !== -1) {
  //   const index = state.albumPages.page.albums.findIndex((v) => v.id === moveItem.id);
  //   const _i = state.albumPages.page.albums.findIndex((v) => v.id === targetPosItem.id);
  //   if (index !== -1 && _i !== -1) {
  //     state.albumPages.page.albums.splice(index, 1);
  //     state.albumPages.page.albums.splice(_i, 0, moveItem);
  //     for (let i = 0; i < page.length; i++) {
  //       if (state.albumPages.page.albums[i] && page[i]) {
  //         state.albumPages.page.albums[i].pageNo = page[i].pageNo;
  //         state.albumPages.page.albums[i].indexes = page[i].indexes;
  //       }
  //     }
  //   }
  // }
};

const addAlbumPage: ActionType<TrimState, { type: PageType, album: TrimPrint }> = (state, action) => {
  // if (action.payload.album.isOption) {
  //   if (state.pageTypeList.opPrint.find(v => v.pageTypeID === action.payload.album.typeID)) {
  //     const find = (() => {
  //       for (const opPrint of state.albumPages.opPrint) {
  //         if (opPrint.xml.viewModel.pageType === action.payload.album.typeID) {
  //           if (!opPrint.album) {
  //             return opPrint;
  //           } else {
  //             if (opPrint.album.id === action.payload.album.id) {
  //               return opPrint;
  //             }
  //           }
  //         }
  //       }
  //     })();
  //     if (find) {
  //       action.payload.album.pageDataId = String(state.albumPages.opPrint.indexOf(find) + 1);
  //       find.album = action.payload.album;
  //     }
  //   } else if (state.pageTypeList.option.find(v => v.pageTypeID === action.payload.album.typeID)) {
  //     const find = state.albumPages.option.find((v) => v.xml.viewModel.pageType === action.payload.album.typeID);
  //     if (find) {
  //       action.payload.album.pageDataId = String(state.albumPages.option.indexOf(find) + 1);
  //       find.album = action.payload.album;
  //     }
  //   }
  // } else {
  //   if (state.pageTypeList.cover.find(v => v.pageTypeID === action.payload.album.typeID)) {
  //     state.albumPages.cover.albums.push(action.payload.album);
  //   } else if (state.pageTypeList.top.find(v => v.pageTypeID === action.payload.album.typeID)) {
  //     state.albumPages.top.albums.push(action.payload.album);
  //   } else if (state.pageTypeList.page.find(v => v.pageTypeID === action.payload.album.typeID)) {
  //     state.albumPages.page.albums.push(action.payload.album);
  //   } else if (state.pageTypeList.endCover.find(v => v.pageTypeID === action.payload.album.typeID)) {
  //     state.albumPages.endCover.albums.push(action.payload.album);
  //   } else {
  //     state.albumPages.page.albums.push(action.payload.album);
  //   }
  // }
};

const removeAlbumPage: ActionType<TrimState, { type: PageType, id: string, typeId: string }> = (state, action) => {
  // const isOption = Boolean(state.pageTypeList.option.find(v => v.pageTypeID === action.payload.typeId))
  const arr = (() => {
    // if (action.payload.type === 'cover') {
    //   return state.albumPages.cover.albums;
    // }
    // if (action.payload.type === 'top') {
    //   return state.albumPages.top.albums;
    // }
    // if (action.payload.type === 'print') {
    //   return state.albumPages.print.albums;
    // }
    // if (action.payload.type === 'end-cover') {
    //   return state.albumPages.endCover.albums;
    // }
    if (state.albumPages.cover.albums.find(v => v.id === action.payload.id)) {
      // if (action.payload.type.indexOf('cover') !== -1) {
      return state.albumPages.cover.albums;
    } else if (state.albumPages.top.albums.find(v => v.id === action.payload.id)) {
      // } else if (action.payload.type.indexOf('top') !== -1) {
      return state.albumPages.top.albums;
    } else if (state.albumPages.page.albums.find(v => v.id === action.payload.id)) {
      // } else if (action.payload.type.indexOf('print') !== -1) {
      return state.albumPages.page.albums;
    } else if (state.albumPages.endCover.albums.find(v => v.id === action.payload.id)) {
      // } else if (action.payload.type.indexOf('end') !== -1) {
      return state.albumPages.endCover.albums;
    } else {
      return state.albumPages.page.albums;
    }
  })();
  const index = arr.findIndex((v) => v.id === action.payload.id);
  if (index !== -1) {
    arr.splice(index, 1);
  } else {
    state.albumPages.option.forEach(v => {
      if (v.album && v.album.id === action.payload.id) {
        v.album = null;
      }
    });
    state.albumPages.opPrint.forEach(v => {
      if (v.album && v.album.id === action.payload.id) {
        v.album = null;
      }
    });
  }
};

const setCurrentAlbumPage: ActionType<TrimState, TrimPrint | null> = (state, action) => {
  state.currentAlbumPage = action.payload;
};

const setThumbnailPath: ActionType<TrimState, string> = (state, action) => {
  state.thumbnailPath = action.payload;
};

const setAlbumSettingData: ActionType<TrimState, PrintSettingData> = (state, action) => {
  state.albumSettingData = action.payload;
};

const addPageRequestMemo: ActionType<TrimState, PageRequestMemo> = (state, action) => {
  const arr = state.pageRequestMemo.filter((v) => v.page !== action.payload.page);
  state.pageRequestMemo = [...arr, action.payload];
};

const addTemplatesInfoList: ActionType<TrimState, TemplatesInfo> = (state, action) => {
  state.templatesInfoList = [...state.templatesInfoList, action.payload];
};

const setTemplatesInfoList: ActionType<TrimState, TemplatesInfo[]> = (state, action) => {
  state.templatesInfoList = action.payload;
};

const setIsDropTemplate: ActionType<TrimState, boolean> = (state, action) => {
  state.isDropTemplate = action.payload;
};

const setIsDraggingTemplate: ActionType<TrimState, boolean> = (state, action) => {
  state.isDraggingTemplate = action.payload;
};

const setAcquiredTemplateList: ActionType<TrimState, AcquiredTempInfo[]> = (state, action) => {
  state.acquiredTemplateList = action.payload;
};

const addAcquiredTemplateList: ActionType<TrimState, AcquiredTempInfo> = (state, action) => {
  state.acquiredTemplateList = [...state.acquiredTemplateList, action.payload];
};

const setQueryParams: ActionType<TrimState, TrimQueryParams | null> = (state, action) => {
  state.queryParams = action.payload;
};

const setPreviewSelectItem: ActionType<TrimState, PreviewSelectItem | null> = (state, action) => {
  state.previewSelectItem = action.payload;
};
const addPresetList: ActionType<TrimState, PresetData> = (state, action) => {
  state.presetList = [...state.presetList, action.payload];
};
const removePresetList: ActionType<TrimState, number> = (state, action) => {
  state.presetList.splice(action.payload, 1);
};
const setPreset: ActionType<TrimState, PresetData> = (state, action) => {
  state.preset = action.payload;
};

const setTemplateGroupName: ActionType<TrimState, string> = (state, action) => {
  state.templateGroupName = action.payload;
};

const addTemplatesXmlList: ActionType<TrimState, any[]> = (state, action) => {
  state.templatesXmlList = [...state.templatesXmlList, ...action.payload];
};

const setTemplatesXmlList: ActionType<TrimState, any[]> = (state, action) => {
  state.templatesXmlList = action.payload;
};

const setDeleteAlbumData: ActionType<TrimState, DeleteTrimPageData[]> = (state, action) => {
  state.deleteAlbumInfos = action.payload;
};

const setOpenTemplateType: ActionType<TrimState, OpenTemplateInfo> = (state, action) => {
  state.openTemplateType = action.payload;
};

const setIsStopExifWarning: ActionType<TrimState, boolean> = (state, action) => {
  state.isStopExifWarning = action.payload;
};

const setIsStopTextPosWarning: ActionType<TrimState, boolean> = (state, action) => {
  state.isStopTextPosWarning = action.payload;
};

const setIsSaving: ActionType<TrimState, boolean> = (state, action) => {
  state.isSaving = action.payload;
};

const setRestoreTemplates: ActionType<TrimState, AddAlbumParam[]> = (state, action) => {
  state.restoreTemplates = action.payload;
};

const setPageDataTypesRes: ActionType<TrimState, PageDataTypesResponse> = (state, action) => {
  state.pageDataTypesRes = action.payload;
};

const setRestorePicData: ActionType<TrimState, RestorePicData[]> = (state, action) => {
  state.restorePicData = action.payload;
};

const setAlertPathList: ActionType<TrimState, AlertPathData[]> = (state, action) => {
  state.alertPathList = action.payload;
};

const setSaveSelect: ActionType<TrimState, SaveSelectData> = (state, action) => {
  state.saveSelect = [...state.saveSelect, action.payload];
};

const setPageTypeList: ActionType<TrimState, any[]> = (state, action) => {
  action.payload.forEach((v) => {
    switch (v.$.type) {
      case 'op_print':
        state.pageTypeList.opPrint = [...state.pageTypeList.opPrint, { pageTypeID: v.$.pageTypeID, type: v.$.type }];
        break;
      case 'cover':
      case 'frame':
        state.pageTypeList.cover = [...state.pageTypeList.cover, { pageTypeID: v.$.pageTypeID, type: v.$.type }];
        break;
      case 'top':
        state.pageTypeList.top = [...state.pageTypeList.top, { pageTypeID: v.$.pageTypeID, type: v.$.type }];
        break;
      case 'page':
        state.pageTypeList.page = [...state.pageTypeList.page, { pageTypeID: v.$.pageTypeID, type: v.$.type }];
        break;
      case 'end':
        state.pageTypeList.endCover = [...state.pageTypeList.endCover, { pageTypeID: v.$.pageTypeID, type: v.$.type }];
        break;
      case  'option':
        state.pageTypeList.option = [...state.pageTypeList.option, { pageTypeID: v.$.pageTypeID, type: v.$.type }];
        break;
      default :
        state.pageTypeList.page = [...state.pageTypeList.page, { pageTypeID: v.$.pageTypeID, type: v.$.type }];
        state.pageTypeList.option = [...state.pageTypeList.option, { pageTypeID: v.$.pageTypeID, type: v.$.type }];
    }
  });
};
const setIsSelectIdError: ActionType<TrimState, boolean> = (state, action) => {
  state.isSelectIDError = action.payload;
};
const setWarningList: ActionType<TrimState, string> = (state, action) => {
  state.warningList = [...state.warningList, action.payload];
};
const removeWarningList: ActionType<TrimState, string> = (state, action) => {
  state.warningList = state.warningList.filter(v => v !== action.payload);
};
const setFitErrorList: ActionType<TrimState, string> = (state, action) => {
  const id = action.payload;
  if (!state.fitErrorList.find((v) => v === id) && id !== '') {
    state.fitErrorList = [...state.fitErrorList, id];
  }
};
const removeFitErrorList: ActionType<TrimState, string> = (state, action) => {
  const id = action.payload;
  state.fitErrorList = state.fitErrorList.filter((v) => v !== id);
};
const setFontErrorList: ActionType<TrimState, string> = (state, action) => {
  state.fontErrorList = [...state.fontErrorList, action.payload];
};
const removeFontErrorList: ActionType<TrimState, string> = (state, action) => {
  state.fontErrorList = state.fontErrorList.filter(v => v !== action.payload);
};
export const trimSlice = createSlice({
  name: 'trim',
  initialState,
  reducers: {
    setTemplateList,
    addTemplateList,
    setFolderList,
    sortAlbumPage,
    addFolderList,
    setSelectFolder,
    setThumbnailPath,
    setPage,
    setOptionName,
    setAlbumData,
    addAlbumPage,
    removeAlbumPage,
    setCurrentAlbumPage,
    setAlbumSettingData,
    addPageRequestMemo,
    addTemplatesInfoList,
    setTemplatesInfoList,
    setIsDropTemplate,
    setIsDraggingTemplate,
    setAcquiredTemplateList,
    addAcquiredTemplateList,
    setQueryParams,
    setPreviewSelectItem,
    addPresetList,
    removePresetList,
    setPreset,
    setTemplateGroupName,
    addTemplatesXmlList,
    setTemplatesXmlList,
    setDeleteAlbumData,
    setOpenTemplateType,
    setIsStopExifWarning,
    setIsStopTextPosWarning,
    setIsSaving,
    setRestoreTemplates,
    setPageDataTypesRes,
    pushUnFindFontList,
    setInitFontList,
    setTrimmingGuideList,
    setRestorePicData,
    setMetaData,
    setAlertPathList,
    setSaveSelect,
    setPageTypeList,
    setIsSelectIdError,
    setWarningList,
    removeWarningList,
    setFitErrorList,
    removeFitErrorList,
    setFontErrorList,
    removeFontErrorList,
  },
});

export const trimActions = trimSlice.actions;
export const trimReducer = trimSlice.reducer;
